/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IconButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import {
    CollapseAllVisibility,
    ColumnActionsMode,
    DetailsRow,
    IColumn,
    IDetailsGroupDividerProps,
    IDetailsHeaderProps,
    IDetailsRowStyleProps,
    IDetailsRowStyles,
    IGroup
} from '@fluentui/react/lib/DetailsList';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Separator } from '@fluentui/react/lib/Separator';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { Sticky } from '@fluentui/react/lib/Sticky';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction, IRenderFunction, SelectionMode } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TranslatorLanguage } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { PanelSettings } from 'src/translation/components/TranslationHistoryPage';
import { TranslationDocumentStore } from 'src/translation/stores/TranslationDocumentStore';
import { TranslationStatusType } from 'src/translation/stores/TranslationJobStore';

export type TranslationHistoryTableProps = {
    supportedLanguages: TranslatorLanguage[];
    selectedJobId: string;
    onChangeSelectedJobId: (jobId: string) => void;
    expandedJobsIds: string[];
    onChangeExpandedJobIds: (jobIds: string[]) => void;
    tableItems: TranslationDocumentStore[];
    tableGroups: Partial<IGroup> & { isCollapsed: boolean }[];
    onChangePanelSettings: (settings: PanelSettings) => void;
    isLoading: boolean;
};

const messages = defineMessages({
    jobIdHeader: {
        id: 'translation.translationHistoryTable.jobIdHeader',
        defaultMessage: 'Translation job id'
    },
    statusHeader: {
        id: 'translation.translationHistoryTable.statusHeader',
        defaultMessage: 'Status'
    },
    sourceLanguageHeader: {
        id: 'translation.translationHistoryTable.sourceLanguageHeader',
        defaultMessage: 'Source language'
    },
    targetLanguagesHeader: {
        id: 'translation.translationHistoryTable.targetLanguagesHeader',
        defaultMessage: 'Target language'
    },
    createdDateHeader: {
        id: 'translation.translationHistoryTable.createdDateHeader',
        defaultMessage: 'Submitted date'
    },
    submittedHeader: {
        id: 'translation.translationHistoryTable.submittedHeader',
        defaultMessage: 'Submitted'
    },
    emptyTablePlaceholder: {
        id: 'translation.translationHistoryTable.emptyTablePlaceholder',
        defaultMessage: 'No translation jobs available'
    },
    viewMoreDetails: {
        id: 'translation.translationHistoryTable.viewMoreDetails',
        defaultMessage: 'View more details'
    },
    downloadFile: {
        id: 'translation.translationHistoryTable.downloadFile',
        defaultMessage: 'Download translated file'
    },
    expandTranslationButton: {
        id: 'translation.translationHistoryTable.expandTranslationButton',
        defaultMessage: 'Expand translation job'
    },
    collapseTranslationButton: {
        id: 'translation.translationHistoryTable.collapseTranslationButton',
        defaultMessage: 'Collapse translation job '
    },
    selectTranslationJob: {
        id: 'translation.translationHistoryTable.selectTranslationJob',
        defaultMessage: 'Select translation job'
    },
    downloadedFileSuccess: {
        id: 'translation.translationHistoryTable.downloadedFileSuccess',
        defaultMessage: 'Downloaded file succeeded'
    },
    downloadedFileFailed: {
        id: 'translation.translationHistoryTable.downloadedFileFailed',
        defaultMessage: 'Downloaded file failed'
    },
    downloadedFileFailedDescription: {
        id: 'translation.translationHistoryTable.downloadedFileFailedDescription',
        defaultMessage: 'The file might be deleted or corrupted'
    },
    downloadFileBtnTitle: {
        id: 'translation.translationHistoryTable.downloadFileBtnTitle',
        defaultMessage: 'Download file'
    }
});
const theme = getScopedTheme('TranslationHistoryPage');

export const failedIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: theme.failureColor, fontSize: '14px' }
});
export const warningIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: theme.warningColor, fontSize: '14px' }
});
export const successIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: theme.successColor, fontSize: '14px' }
});
const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { height: '100%', position: 'relative' }
});

const tableTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }
});

const tableFileNameColumnStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: {
        overflow: 'hidden',
        whiteSpace: 'normal',
        marginLeft: '30px',
        height: 'fit-content'
    }
});

const classNames = mergeStyleSets({
    fileIconHeaderIcon: { padding: 0, fontSize: '16px' },
    fileIconCell: {
        textAlign: 'center',
        selectors: {
            '&:before': {
                content: '.',
                width: '0px',
                height: '100%',
                visibility: 'hidden',
                display: 'inline-block',
                verticalAlign: 'middle'
            }
        }
    }
});

const rowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    cell: { display: 'inline-flex', alignItems: 'center' },
    root: {
        height: '64px',
        width: '100%',
        marginBottom: '8px',
        backgroundColor: theme.rowBackgroundColor,
        borderBottom: theme.groupBorder
    }
});

export const TranslationHistoryTable = observer((props: TranslationHistoryTableProps) => {
    const {
        supportedLanguages,
        selectedJobId,
        onChangeSelectedJobId,
        expandedJobsIds,
        onChangeExpandedJobIds,
        onChangePanelSettings,
        tableGroups,
        tableItems,
        isLoading
    } = props;

    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();

    const expandedJobStores = rootStore.translationJobStoreHouse.items.filter(job =>
        expandedJobsIds.some(expandedJobId => expandedJobId === job.id)
    );

    const downloadFile = (fileName: string, storageAccountName: string, containerName: string, folderName: string) => {
        logDTButtonClick('document translation', 'jobHistory', 'Button', 'downloadFile');
        dispatcher.dispatch('downloadFile', {
            fileName,
            storageAccountName,
            containerName,
            folderName,
            toastData: {
                failed: { title: localize(messages.downloadedFileFailed), content: localize(messages.downloadedFileFailedDescription) },
                completed: { title: localize(messages.downloadedFileSuccess) }
            }
        });
    };

    const tableColumns: IColumn[] = [
        {
            minWidth: 16,
            maxWidth: 16,
            key: 'fileIcon',
            iconName: 'Page',
            isIconOnly: true,
            name: 'File icon',
            className: classNames.fileIconCell,
            iconClassName: classNames.fileIconHeaderIcon
        },
        {
            minWidth: 350,
            maxWidth: 600,
            key: 'fileName',
            fieldName: 'fileName',
            isResizable: true,
            name: localize(messages.jobIdHeader),
            onRender: (item: TranslationDocumentStore) => (
                <Stack
                    horizontal
                    verticalAlign="center"
                    title={item.fileName}
                    tokens={{ childrenGap: 20, maxWidth: '100%' }}
                    styles={tableFileNameColumnStyles}>
                    <LuisText title={item.fileName} styles={tableTextStyles}>
                        {item.fileName}
                    </LuisText>
                </Stack>
            )
        },
        {
            minWidth: 270,
            maxWidth: 650,
            key: 'status',
            fieldName: 'status',
            name: localize(messages.statusHeader),
            isResizable: true,
            onRender: (item: TranslationDocumentStore) => (
                <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center" styles={tableTextStyles}>
                    {renderStatus(item.status, true)}
                    {item.error && (
                        <Stack
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: 2 }}
                            styles={{ root: { overflow: 'hidden', whiteSpace: 'normal' } }}>
                            <Separator vertical />
                            <LuisText styles={tableTextStyles} title={item.error}>
                                {item.error}
                            </LuisText>
                        </Stack>
                    )}
                </Stack>
            )
        },
        // Remove the source language column until the APIs return them
        // {
        //     minWidth: 130,
        //     maxWidth: 160,
        //     key: 'sourceLanguageCode',
        //     fieldName: 'sourceLanguageCode',
        //     isResizable: true,
        //     name: localize(messages.sourceLanguageHeader),
        //     onRender: (item: TranslationDocumentStore) => (
        //         <LuisText title={item.targetLanguageCode} styles={tableTextStyles}>
        //             {supportedLanguages.find(l => l.code === item.sourceLanguageCode)?.name ?? 'N/A'}
        //         </LuisText>
        //     )
        // },
        {
            minWidth: 140,
            maxWidth: 160,
            key: 'targetLanguageCode',
            fieldName: 'targetLanguageCode',
            isResizable: true,
            name: localize(messages.targetLanguagesHeader),
            onRender: (item: TranslationDocumentStore) => (
                <LuisText title={item.targetLanguageCode} styles={tableTextStyles}>
                    {supportedLanguages.find(l => l.code === item.targetLanguageCode)?.name || item.targetLanguageCode}
                </LuisText>
            )
        },
        {
            minWidth: 140,
            maxWidth: 150,
            key: 'createdDateTime',
            fieldName: 'createdDateTime',
            isResizable: true,
            name: localize(messages.createdDateHeader),
            onRender: (item: TranslationDocumentStore) => (
                <LuisText title={item.createdDateTime.toLocaleString()} styles={tableTextStyles}>
                    {item.createdDateTime.toLocaleString([], {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </LuisText>
            )
        },
        {
            key: 'downloadButton',
            name: 'downloadButton',
            minWidth: 15,
            maxWidth: 15,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: TranslationDocumentStore) => (
                <IconButton
                    id={'downloadButton'}
                    iconProps={{ iconName: 'Download' }}
                    title={localize(messages.downloadFileBtnTitle)}
                    ariaLabel={localize(messages.downloadFileBtnTitle)}
                    data-automation-id="downloadButton"
                    disabled={!!item.error || item.status !== 'Succeeded'}
                    onClick={() =>
                        downloadFile(
                            item.destinationFileName,
                            item.destinationStorageAccountName,
                            item.destinationContainerName,
                            item.destinationFolderName
                        )
                    }
                />
            )
        }
    ];

    const onHandleRowExpansionClicked = (groupProps: IDetailsGroupDividerProps) => {
        let newJobs: string[] = [];
        if (isGroupExpanded(groupProps.group.data.jobId)) {
            newJobs = expandedJobsIds.filter(j => j !== groupProps.group.data.jobId);
        } else {
            newJobs = [...expandedJobsIds, groupProps.group.data.jobId];
        }
        onChangeExpandedJobIds(newJobs);
    };

    const getViewMoreMenuItem = (groupProps: IDetailsGroupDividerProps) => ({
        id: 'menuItems',
        items: [
            {
                key: 'viewMore',
                role: 'viewMore',
                text: localize(messages.viewMoreDetails),
                ariaLabel: localize(messages.viewMoreDetails),
                onClick: () => {
                    onChangePanelSettings({ isOpen: true, selectedJobId: groupProps.group.name });
                }
            }
        ],
        ariaLabel: localize(messages.viewMoreDetails)
    });

    const isGroupExpanded = (jobId: string) => expandedJobsIds.includes(jobId);

    const renderStatusIcon = (status: TranslationStatusType) => {
        switch (status) {
            case 'Failed':
                return <Icon iconName="Warning12" styles={failedIconStyles} />;
            case 'Succeeded':
                return <Icon iconName="Completed" styles={successIconStyles} />;
            case 'ValidationFailed':
                return <Icon iconName="Warning12" styles={warningIconStyles} />;
            case 'Cancelled':
                return <Icon iconName="Info" styles={warningIconStyles} />;
            case 'NotStarted':
            case 'Running':
            case 'Cancelling':
                return <Spinner size={SpinnerSize.small} />;
            default:
                throw new Error('Not a valid status type');
        }
    };
    const renderStatus = (status: TranslationStatusType, isDocument?: boolean) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 2 }}>
            {renderStatusIcon(status)}
            <Stack>
                <LuisText variant={isDocument ? 'medium' : 'smallPlus'}>{status}</LuisText>
            </Stack>
        </Stack>
    );

    const onRenderHeader: IRenderFunction<IDetailsHeaderProps> = (headerProps, defaultHeaderRenderer) => (
        <Sticky>
            {defaultHeaderRenderer({ ...headerProps })}
            {/* <DetailsHeader {...headerProps} styles={detailedHeaderStyles} /> */}
        </Sticky>
    );

    const onRenderGroupHeader = (groupProps: IDetailsGroupDividerProps) => (
        <Stack horizontal styles={{ root: { alignItems: 'center', borderBottom: theme.groupBorder } }}>
            <Checkbox
                checked={groupProps.group.name === selectedJobId}
                onChange={() => {
                    if (groupProps.group.name === selectedJobId) {
                        onChangeSelectedJobId(null);
                    } else {
                        onChangeSelectedJobId(groupProps.group.name);
                    }
                }}
                styles={{ checkbox: { height: '16px', width: '16px', margin: '0 8px 0 12px' } }}
                ariaLabel={localize(messages.selectTranslationJob)}
            />

            {groupProps.group.count &&
            (!isGroupExpanded(groupProps.group.data.jobId) ||
                expandedJobStores.find(j => j.id === groupProps.group.data.jobId)?.documentStoreHouse.tracker.isCompleted) ? (
                <IconButton
                    disabled={!groupProps.group.count}
                    onClick={() => onHandleRowExpansionClicked(groupProps)}
                    ariaLabel={
                        isGroupExpanded(groupProps.group.data.jobId)
                            ? localize(messages.collapseTranslationButton)
                            : localize(messages.expandTranslationButton)
                    }
                    styles={{ root: { background: theme.disabledIconBtnColor } }}
                    iconProps={{
                        iconName: isGroupExpanded(groupProps.group.data.jobId) ? 'ChevronDown' : 'ChevronRight'
                    }}
                    title={
                        isGroupExpanded(groupProps.group.data.jobId)
                            ? localize(messages.collapseTranslationButton)
                            : localize(messages.expandTranslationButton)
                    }
                />
            ) : (
                <Spinner size={SpinnerSize.small} styles={{ root: { padding: '0 8px' } }} />
            )}

            <Stack tokens={{ childrenGap: 5 }} styles={{ root: { padding: '10px' } }}>
                <Stack horizontal styles={{ root: { alignItems: 'center' } }}>
                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {groupProps.group.data.jobId} ({groupProps.group.count})
                    </LuisText>
                </Stack>
                <Stack horizontal styles={{ root: { alignItems: 'center' } }} tokens={{ childrenGap: 5 }}>
                    {renderStatus(groupProps.group.data.status)}
                    <LuisText>|</LuisText>
                    <LuisText variant="smallPlus">
                        {groupProps.group.data.createdDateTime.toLocaleString([], {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </LuisText>
                    <LuisText>|</LuisText>
                    <IconButton
                        iconProps={{ iconName: 'More' }}
                        menuIconProps={{ style: { display: 'none' } }}
                        ariaLabel={localize(messages.viewMoreDetails)}
                        menuProps={getViewMoreMenuItem(groupProps)}
                    />
                </Stack>
            </Stack>
        </Stack>
    );

    return (
        <ScrollablePane styles={mergeStyleSets(ScrollablePaneStyles, { root: { minHeight: '450px' } })}>
            <Table<TranslationDocumentStore>
                items={tableItems}
                isLoading={isLoading}
                columns={tableColumns}
                groups={tableGroups as IGroup[]}
                selectionMode={SelectionMode.none}
                onRenderDetailsHeader={onRenderHeader}
                styles={{ root: { maxWidth: '1200px' } }}
                emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
                onRenderRow={rowProps => <DetailsRow {...rowProps} styles={rowStyles} />}
                groupProps={{
                    showEmptyGroups: true,
                    onRenderHeader: onRenderGroupHeader,
                    collapseAllVisibility: CollapseAllVisibility.hidden
                }}
            />
        </ScrollablePane>
    );
});
